"use client";

import { useState } from "react";
import {
	type AlertColor,
	type AlertSeverity,
	AlertTitle,
	GlobalAlert,
} from "@leabankab/component-library";
import { StrapiBlocks } from "@/components/ui/strapi-blocks";
import type { AppAlertContent } from "@/types/cms";
import {
	getAppAlertRevision,
	writeDismissedAppAlert,
} from "@/utils/app-alert-dismissal";

type AlertStyle = {
	/** Drives the icon and screen reader announcement; only the four semantic ones exist. */
	severity: AlertSeverity;
	color: AlertColor;
};

const SEVERITY: Record<AppAlertContent["severity"], AlertStyle> = {
	error: { severity: "error", color: "error" },
	warning: { severity: "warning", color: "warning" },
	info: { severity: "info", color: "info" },
	success: { severity: "success", color: "success" },
	brand: { severity: "info", color: "primary" },
	grey: { severity: "info", color: "neutral" },
};

type Props = {
	alert: AppAlertContent | null;
	/** Read from the dismissal cookie during SSR, so a dismissed alert is never rendered. */
	initiallyDismissed: boolean;
};

export const AppAlert = ({ alert, initiallyDismissed }: Props) => {
	const revision = alert ? getAppAlertRevision(alert) : null;
	const [dismissedRevision, setDismissedRevision] = useState(
		initiallyDismissed ? revision : null
	);

	if (!alert || dismissedRevision === revision) return null;

	const { severity, color } = SEVERITY[alert.severity];

	const dismiss = () => {
		writeDismissedAppAlert(alert);
		setDismissedRevision(revision);
	};

	return (
		<GlobalAlert
			severity={severity}
			color={color}
			role="status"
			size="sm"
			onClose={alert.closable ? dismiss : undefined}
			innerClassName="max-w-2xl"
		>
			{alert.showTitle && alert.title ? (
				<AlertTitle>{alert.title}</AlertTitle>
			) : null}
			<StrapiBlocks nodes={alert.body} variant="body2" />
		</GlobalAlert>
	);
};
